import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tokens } from '../../theme';
import { PublicNav } from '../../components/nav/PublicNav';
import { Skeleton } from '../../components/Skeleton';
import { publicApi } from '../../lib/api';

interface CaseStudy {
  id: string;
  brand_name: string;
  title: string;
  public_headline?: string | null;
  public_body_md?: string | null;
  hero_image_key?: string | null;
  vertical?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  total_verified_views?: number;
}

interface PublicClip {
  id: string;
  platform: string;
  submitted_url: string;
  public_caption?: string | null;
  verified_views: number;
}

export function WorkDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<{ campaign: CaseStudy; clips: PublicClip[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('studio-blue');
    return () => document.body.classList.remove('studio-blue');
  }, []);

  useEffect(() => {
    if (!slug) return;
    publicApi
      .campaign(slug)
      .then((d) => setData(d as unknown as { campaign: CaseStudy; clips: PublicClip[] }))
      .catch(() => setError('not_found'));
  }, [slug]);

  if (error) {
    return (
      <>
        <PublicNav />
        <main
          style={{
            padding: '160px 48px 96px',
            maxWidth: 720,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontFamily: tokens.font.display, fontSize: 40, fontWeight: 400 }}>
            Not here.
          </h1>
          <p style={{ marginTop: 16, color: tokens.color.ink[500] }}>
            This work may have been retired.
          </p>
        </main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <PublicNav />
        <main style={{ padding: '160px 48px 96px', maxWidth: 1280, margin: '0 auto' }}>
          <Skeleton height={48} width="60%" />
          <div style={{ marginTop: 24 }}>
            <Skeleton height={400} radius={12} />
          </div>
        </main>
      </>
    );
  }

  const c = data.campaign;
  return (
    <>
      <PublicNav onDarkHero />
      <header
        style={{
          minHeight: '70vh',
          position: 'relative',
          background: tokens.color.night[900],
          color: '#fff',
          backgroundImage: c.hero_image_key
            ? `url(https://studio-assets.imorbis.com/${c.hero_image_key})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(22,30,41,0.6) 0%, rgba(22,30,41,0.3) 100%)',
          }}
        />
        <div
          style={{
            position: 'relative',
            padding: '160px 48px 80px',
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          <span
            style={{
              fontSize: 11,
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              color: tokens.color.ink[300],
            }}
          >
            {c.brand_name}
          </span>
          <h1
            style={{
              fontFamily: tokens.font.display,
              fontSize: 72,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              fontStyle: 'normal',
              marginTop: 16,
              maxWidth: 900,
            }}
          >
            {c.title}
          </h1>
          {c.public_headline && (
            <p
              style={{
                fontSize: 19,
                color: tokens.color.ink[100],
                marginTop: 24,
                maxWidth: 720,
              }}
            >
              {c.public_headline}
            </p>
          )}
        </div>
      </header>

      <section style={{ padding: '64px 48px', background: tokens.color.paper[100] }}>
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'flex',
            gap: 48,
            flexWrap: 'wrap',
          }}
        >
          <Stat
            label="Verified views"
            value={c.total_verified_views?.toLocaleString() ?? '0'}
          />
          <Stat label="Vertical" value={c.vertical ?? 'general'} />
          {c.starts_at && (
            <Stat
              label="Window"
              value={`${c.starts_at.slice(0, 10)} to ${c.ends_at?.slice(0, 10) ?? 'open'}`}
            />
          )}
        </div>
      </section>

      {c.public_body_md && (
        <section style={{ padding: '64px 48px', background: tokens.color.paper['00'] }}>
          <div
            style={{
              maxWidth: 720,
              margin: '0 auto',
              fontSize: 19,
              fontWeight: 300,
              lineHeight: 1.6,
              color: tokens.color.ink[700],
              whiteSpace: 'pre-wrap',
            }}
          >
            {c.public_body_md}
          </div>
        </section>
      )}

      {data.clips.length > 0 && (
        <section style={{ padding: '64px 48px', background: tokens.color.paper[100] }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.35em',
                textTransform: 'uppercase',
                color: tokens.color.ink[500],
              }}
            >
              Clip Gallery
            </span>
            <div
              style={{
                marginTop: 32,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 24,
              }}
            >
              {data.clips.map((cl) => (
                <a
                  key={cl.id}
                  href={cl.submitted_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: tokens.color.paper['00'],
                    border: `1px solid ${tokens.color.line[100]}`,
                    borderRadius: 12,
                    padding: 20,
                    display: 'block',
                  }}
                >
                  <span
                    style={{
                      fontFamily: tokens.font.mono,
                      fontSize: 11,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: tokens.color.ink[500],
                    }}
                  >
                    {cl.platform}
                  </span>
                  {cl.public_caption && (
                    <p style={{ fontSize: 14, color: tokens.color.ink[900], marginTop: 8 }}>
                      {cl.public_caption}
                    </p>
                  )}
                  <span
                    style={{
                      fontFamily: tokens.font.mono,
                      fontSize: 13,
                      color: tokens.color.ink[700],
                      marginTop: 12,
                      display: 'block',
                    }}
                  >
                    {cl.verified_views?.toLocaleString() ?? 0} views
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span
        style={{
          fontSize: 11,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
        }}
      >
        {label}
      </span>
      <div
        style={{
          fontFamily: tokens.font.display,
          fontSize: 40,
          fontWeight: 400,
          color: tokens.color.ink[900],
          marginTop: 8,
        }}
      >
        {value}
      </div>
    </div>
  );
}
